import { Achievement, GameState } from '../types';
import { ConditionEvaluator } from './condition-evaluator';

/**
 * AchievementManager
 * 
 * Handles evaluation and tracking of game achievements.
 * Uses ConditionEvaluator to check if achievement conditions are met.
 */
export class AchievementManager {
  /**
   * Evaluates all achievements in the scenario against the current game state.
   * Returns only achievements that are newly unlocked (met condition and not previously unlocked).
   * 
   * @param achievements - All available achievements in the current scenario
   * @param gameState - The current game state
   * @returns Array of newly unlocked achievements
   */
  static evaluateAchievements(achievements: Achievement[], gameState: GameState): Achievement[] {
    if (!achievements || achievements.length === 0) {
      return [];
    }

    const newlyUnlocked: Achievement[] = [];
    const unlockedIds = new Set(gameState.unlockedAchievements || []);

    for (const achievement of achievements) {
      // Skip if already unlocked
      if (unlockedIds.has(achievement.id)) {
        continue;
      }

      // Check if condition is met
      try {
        if (ConditionEvaluator.evaluate(achievement.condition, gameState)) {
          newlyUnlocked.push(achievement);
        }
      } catch (error) {
        console.error(`Error evaluating achievement "${achievement.id}":`, error);
      }
    }

    return newlyUnlocked;
  }

  /**
   * Checks if a specific achievement is unlocked in the current game state.
   * 
   * @param achievementId - The ID of the achievement to check
   * @param gameState - The current game state
   * @returns true if unlocked, false otherwise
   */
  static isUnlocked(achievementId: string, gameState: GameState): boolean {
    return (gameState.unlockedAchievements || []).includes(achievementId);
  }

  /**
   * Gets the current achievement progress.
   * 
   * @param achievements - All available achievements in the current scenario
   * @param gameState - The current game state
   * @returns Object containing unlocked count and total count
   */
  static getProgress(achievements: Achievement[], gameState: GameState): { unlocked: number; total: number } {
    return {
      unlocked: (gameState.unlockedAchievements || []).length,
      total: (achievements || []).length
    };
  }
}
